"use client";

import { ArrowRight, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function BroadcastForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-primary bg-background/40 backdrop-blur-md text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-primary mb-4">
          MESSAGE RECEIVED 
        </p>
        <p className="font-serif text-xl text-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="font-default font-bold uppercase tracking-wider border-foreground text-foreground hover:bg-foreground hover:text-background rounded-md">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Min 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
                Full Name *
              </FormLabel>
              <FormControl>
                <Input placeholder="Captain John Doe" {...field} className="bg-transparent border-0 border-b border-foreground/40 rounded-md px-0 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-2px_var(--primary)] transition-all" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
                Email Address *
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="captain@example.com" {...field} className="bg-transparent border-0 border-b border-foreground/40 rounded-md px-0 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-2px_var(--primary)] transition-all" />
              </FormControl>
              <FormMessage className="font-mono text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+]?[\d\s()-]{7,}$/,
        message: "Invalid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (228) 555-0199" {...field} className="bg-transparent border-0 border-b border-foreground/40 rounded-md px-0 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-2px_var(--primary)] transition-all" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              Inquiry Type *
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b border-foreground/40 rounded-md px-0 py-3 h-auto text-foreground data-[placeholder]:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 w-full">
                  <SelectValue placeholder="Select an inquiry type" />
                </SelectTrigger>
                <SelectContent className="bg-popover border-border rounded-md">
                  <SelectItem value="General Inquiry">General Inquiry</SelectItem>
                  <SelectItem value="Wholesale Account">Wholesale Account</SelectItem>
                  <SelectItem value="Order Status">Order Status</SelectItem>
                  <SelectItem value="Sustainability Questions">Sustainability Questions</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-[11px] uppercase tracking-widest text-primary">
              Your Message
            </FormLabel>
            <FormControl>
              <Textarea placeholder="How can we help you today?" {...field} className="bg-transparent border-0 border-b border-foreground/40 rounded-md px-0 py-3 h-auto text-foreground placeholder:text-foreground/30 focus-visible:border-primary focus-visible:ring-0 focus-visible:shadow-[0_4px_12px_-2px_var(--primary)] transition-all" />
            </FormControl>
            <FormMessage className="font-mono text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="font-mono text-xs uppercase tracking-widest text-destructive">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-auto bg-primary text-primary-foreground font-default font-bold uppercase tracking-wider text-base py-5 rounded-md hover:shadow-[0_0_0_0_hsl(0_0%_0%)] hover:translate-x-[6px] hover:translate-y-[6px] transition-all duration-150 hover:bg-primary">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : <>
            Send Message
            <ArrowRight className="ml-2 size-5" />
          </>}
      </Button>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="2593" id="get-in-touch" className="relative dark bg-background text-foreground py-32 md:py-[200px] px-4 sm:px-6 lg:px-8 overflow-hidden">
      <div className="hidden lg:block absolute left-8 top-1/2 -translate-y-1/2 pointer-events-none">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-foreground/40 [writing-mode:vertical-rl] rotate-180">
          CONTACT 
        </div>
      </div>

      <div className="hidden lg:block absolute right-8 top-1/2 -translate-y-1/2 pointer-events-none">
        <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-foreground/40 [writing-mode:vertical-rl]">
          CONTACT 
        </div>
      </div>

      <div ref={ref} className={`max-w-7xl mx-auto transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="flex flex-col items-center text-center mb-12 md:mb-16">
          <div className="h-px w-[120px] bg-primary mb-6" />
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-foreground/60 mb-8">
            GET IN TOUCH 
          </p>
          <h2 className="font-default font-black uppercase tracking-tighter leading-[0.9] text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[8rem] text-foreground">
            CONTACT THE{" "}
            <span className="text-primary">MISS YVONNE.</span>
          </h2>
          <p className="font-serif text-lg md:text-xl text-muted-foreground max-w-[600px] mt-8 leading-relaxed">
            Built by Hand. Proven by Time. Reach out to our team for inquiries regarding our wild-caught seafood, wholesale accounts, or delivery services.
          </p>
        </div>

        <div className="max-w-[720px] mx-auto">
          <div className="relative bg-background/40 backdrop-blur-md border border-foreground/10 p-8 sm:p-12 md:p-16 rounded-md">
            <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="get-in-touch" defaultValues={{
            name: "",
            email: "",
            phone: "",
            inquiry_type: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            inquiry_type: {
              required: true
            }
          }} submitText="Send Message" successMessage="Message received. Our crew will respond to your inquiry shortly." errorMessage="Transmission failed. Please check your connection and try again.">
              <BroadcastForm />
            </FormProvider>
          </div>

          <div className="flex items-center justify-center gap-3 mt-8">
            <div className="h-px w-12 bg-foreground/30" />
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-foreground/50">
              EST. 1965 
            </p>
            <div className="h-px w-12 bg-foreground/30" />
          </div>
        </div>
      </div>
    </section>;
}